import type { ComponentProps } from 'react';
import { cn } from '../lib/cn.ts';

/*
 * The product's icon set, and only the product's.
 *
 * Read out of vendy.ai's own bundle rather than pulled from a package: the
 * app code-splits every Lucide glyph into its own module, so what is here is
 * exactly what the product draws, at the version it draws it — plus the four
 * it drew itself (the two ad types, generations, and the credits bolt), which
 * exist in no icon set.
 *
 * Shipping it here rather than leaving each screen to import from `lucide-react`
 * is what keeps a mockup honest: `scratchpad/` may import `@starter/ui` and
 * nothing else, so an icon that is not here is an icon a mockup cannot draw —
 * which is the same constraint the product has, for the same reason.
 *
 * Two kinds, and the kind is not cosmetic: Lucide's are strokes on a 24 grid,
 * Vendy's four are filled shapes, and the bolt is drawn on a 20 grid. Rendering
 * one as the other is a smudge or a wrong size, never an error.
 */

interface Glyph {
  /** [tag, attributes] — path, circle, rect or line. */
  e: [string, Record<string, string>][];
  /** Filled rather than stroked. */
  f?: 1;
  /** viewBox edge; 24 unless stated. */
  b?: number;
}

const GLYPHS = {
  'arrow-down': {
    e: [
      ['path', { d: 'M12 5v14' }],
      ['path', { d: 'm19 12-7 7-7-7' }],
    ],
  },
  'arrow-left': {
    e: [
      ['path', { d: 'm12 19-7-7 7-7' }],
      ['path', { d: 'M19 12H5' }],
    ],
  },
  'arrow-right': {
    e: [
      ['path', { d: 'M5 12h14' }],
      ['path', { d: 'm12 5 7 7-7 7' }],
    ],
  },
  'arrow-up-down': {
    e: [
      ['path', { d: 'm21 16-4 4-4-4' }],
      ['path', { d: 'M17 20V4' }],
      ['path', { d: 'm3 8 4-4 4 4' }],
      ['path', { d: 'M7 4v16' }],
    ],
  },
  bell: {
    e: [
      ['path', { d: 'M10.268 21a2 2 0 0 0 3.464 0' }],
      [
        'path',
        {
          d: 'M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326',
        },
      ],
      ['path', { d: 'm11 10 3 3' }],
      ['path', { d: 'M6.5 21A3.5 3.5 0 1 0 3 17.5a2.62 2.62 0 0 1-.708 1.792A1 1 0 0 0 3 21z' }],
      ['path', { d: 'M9.969 17.031 21.378 5.624a1 1 0 0 0-3.002-3.002L6.967 14.031' }],
      ['circle', { cx: '12', cy: '12', r: '10' }],
      ['circle', { cx: '12', cy: '10', r: '3' }],
      ['path', { d: 'M7 20.662V19a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v1.662' }],
      ['path', { d: 'm15 15 6 6' }],
      ['path', { d: 'm15 9 6-6' }],
      ['path', { d: 'M21 16v5h-5' }],
      ['path', { d: 'M21 8V3h-5' }],
      ['path', { d: 'M3 16v5h5' }],
      ['path', { d: 'm3 21 6-6' }],
      ['path', { d: 'M3 8V3h5' }],
      ['path', { d: 'M9 9 3 3' }],
      [
        'path',
        {
          d: 'M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z',
        },
      ],
      ['path', { d: 'M12 7v14' }],
      ['path', { d: 'M20 11v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8' }],
      ['path', { d: 'M7.5 7a1 1 0 0 1 0-5A4.8 8 0 0 1 12 7a4.8 8 0 0 1 4.5-5 1 1 0 0 1 0 5' }],
      ['rect', { x: '3', y: '7', width: '18', height: '4', rx: '1' }],
      ['path', { d: 'M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8' }],
      [
        'path',
        {
          d: 'M3 10a2 2 0 0 1 .709-1.528l7-6a2 2 0 0 1 2.582 0l7 6A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
        },
      ],
      ['path', { d: 'm22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7' }],
      ['rect', { x: '2', y: '4', width: '20', height: '16', rx: '2' }],
      ['path', { d: 'M8 3H5a2 2 0 0 0-2 2v3' }],
      ['path', { d: 'M21 8V5a2 2 0 0 0-2-2h-3' }],
      ['path', { d: 'M3 16v3a2 2 0 0 0 2 2h3' }],
      ['path', { d: 'M16 21h3a2 2 0 0 0 2-2v-3' }],
      ['path', { d: 'M8 3v3a2 2 0 0 1-2 2H3' }],
      ['path', { d: 'M21 8h-3a2 2 0 0 1-2-2V3' }],
      ['path', { d: 'M3 16h3a2 2 0 0 1 2 2v3' }],
      ['path', { d: 'M16 21v-3a2 2 0 0 1 2-2h3' }],
      ['rect', { width: '20', height: '14', x: '2', y: '3', rx: '2' }],
      ['line', { x1: '8', x2: '16', y1: '21', y2: '21' }],
      ['line', { x1: '12', x2: '12', y1: '17', y2: '21' }],
      ['rect', { width: '18', height: '18', x: '3', y: '3', rx: '2' }],
      ['path', { d: 'M9 3v18' }],
      [
        'path',
        {
          d: 'm12 9-8.414 8.414A2 2 0 0 0 3 18.828v1.344a2 2 0 0 1-.586 1.414A2 2 0 0 1 3.828 21h1.344a2 2 0 0 0 1.414-.586L15 12',
        },
      ],
      [
        'path',
        { d: 'm18 9 .4.4a1 1 0 1 1-3 3l-3.8-3.8a1 1 0 1 1 3-3l.4.4 3.4-3.4a1 1 0 1 1 3 3z' },
      ],
      ['path', { d: 'm2 22 .414-.414' }],
      ['path', { d: 'M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7' }],
      ['path', { d: 'M14 15H9v-5' }],
      ['path', { d: 'M16 3h5v5' }],
      ['path', { d: 'M21 3 9 15' }],
    ],
  },
  box: {
    e: [
      [
        'path',
        {
          d: 'M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z',
        },
      ],
      ['path', { d: 'm3.3 7 8.7 5 8.7-5' }],
      ['path', { d: 'M12 22V12' }],
    ],
  },
  'chart-column': {
    e: [
      ['path', { d: 'M3 3v16a2 2 0 0 0 2 2h16' }],
      ['path', { d: 'M18 17V9' }],
      ['path', { d: 'M13 17V5' }],
      ['path', { d: 'M8 17v-3' }],
    ],
  },
  check: { e: [['path', { d: 'M20 6 9 17l-5-5' }]] },
  'chevron-down': { e: [['path', { d: 'm6 9 6 6 6-6' }]] },
  'chevron-right': { e: [['path', { d: 'm9 18 6-6-6-6' }]] },
  'chevron-up': { e: [['path', { d: 'm18 15-6-6-6 6' }]] },
  'circle-alert': {
    e: [
      ['circle', { cx: '12', cy: '12', r: '10' }],
      ['line', { x1: '12', x2: '12', y1: '8', y2: '12' }],
      ['line', { x1: '12', x2: '12.01', y1: '16', y2: '16' }],
    ],
  },
  'circle-check': {
    e: [
      ['circle', { cx: '12', cy: '12', r: '10' }],
      ['path', { d: 'm9 12 2 2 4-4' }],
    ],
  },
  'circle-question-mark': {
    e: [
      ['circle', { cx: '12', cy: '12', r: '10' }],
      ['path', { d: 'M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3' }],
      ['path', { d: 'M12 17h.01' }],
    ],
  },
  'circle-x': {
    e: [
      ['circle', { cx: '12', cy: '12', r: '10' }],
      ['path', { d: 'm15 9-6 6' }],
      ['path', { d: 'm9 9 6 6' }],
    ],
  },
  'cloud-upload': {
    e: [
      ['path', { d: 'M12 13v8' }],
      ['path', { d: 'M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242' }],
      ['path', { d: 'm8 17 4-4 4 4' }],
    ],
  },
  copy: {
    e: [
      ['rect', { width: '14', height: '14', x: '8', y: '8', rx: '2', ry: '2' }],
      ['path', { d: 'M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2' }],
    ],
  },
  cpu: {
    e: [
      ['path', { d: 'M12 20v2' }],
      ['path', { d: 'M12 2v2' }],
      ['path', { d: 'M17 20v2' }],
      ['path', { d: 'M17 2v2' }],
      ['path', { d: 'M2 12h2' }],
      ['path', { d: 'M2 17h2' }],
      ['path', { d: 'M2 7h2' }],
      ['path', { d: 'M20 12h2' }],
      ['path', { d: 'M20 17h2' }],
      ['path', { d: 'M20 7h2' }],
      ['path', { d: 'M7 20v2' }],
      ['path', { d: 'M7 2v2' }],
      ['rect', { x: '4', y: '4', width: '16', height: '16', rx: '2' }],
      ['rect', { x: '8', y: '8', width: '8', height: '8', rx: '1' }],
    ],
  },
  download: {
    e: [
      ['path', { d: 'M12 15V3' }],
      ['path', { d: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' }],
      ['path', { d: 'm7 10 5 5 5-5' }],
    ],
  },
  ellipsis: {
    e: [
      ['circle', { cx: '12', cy: '12', r: '1' }],
      ['circle', { cx: '19', cy: '12', r: '1' }],
      ['circle', { cx: '5', cy: '12', r: '1' }],
    ],
  },
  eraser: {
    e: [
      [
        'path',
        {
          d: 'M21 21H8a2 2 0 0 1-1.42-.587l-3.994-3.999a2 2 0 0 1 0-2.828l10-10a2 2 0 0 1 2.829 0l5.999 6a2 2 0 0 1 0 2.828L12.834 21',
        },
      ],
      ['path', { d: 'm5.082 11.09 8.828 8.828' }],
      ['path', { d: 'M9 18V5l12-2v13' }],
      ['circle', { cx: '6', cy: '18', r: '3' }],
      ['circle', { cx: '18', cy: '16', r: '3' }],
    ],
  },
  'external-link': {
    e: [
      ['path', { d: 'M15 3h6v6' }],
      ['path', { d: 'M10 14 21 3' }],
      ['path', { d: 'M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6' }],
    ],
  },
  eye: {
    e: [
      [
        'path',
        {
          d: 'M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0',
        },
      ],
      ['circle', { cx: '12', cy: '12', r: '3' }],
    ],
  },
  'eye-off': {
    e: [
      [
        'path',
        {
          d: 'M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49',
        },
      ],
      ['path', { d: 'M14.084 14.158a3 3 0 0 1-4.242-4.242' }],
      [
        'path',
        {
          d: 'M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143',
        },
      ],
      ['path', { d: 'm2 2 20 20' }],
    ],
  },
  'file-text': {
    e: [
      [
        'path',
        {
          d: 'M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z',
        },
      ],
      ['path', { d: 'M14 2v5a1 1 0 0 0 1 1h5' }],
      ['path', { d: 'M10 9H8' }],
      ['path', { d: 'M16 13H8' }],
      ['path', { d: 'M16 17H8' }],
    ],
  },
  generations: {
    e: [
      [
        'path',
        {
          d: 'M10.3633 1.70057C10.7725 1.58829 11.1722 1.59773 11.5654 1.64588C11.9471 1.69266 12.4053 1.78825 12.9365 1.89784L18.4258 3.02967C18.9569 3.13923 19.4154 3.23315 19.7842 3.3412C20.1642 3.45259 20.535 3.6026 20.8662 3.86756C21.3607 4.26325 21.7181 4.80662 21.8857 5.41737C21.9978 5.82627 21.9885 6.22557 21.9404 6.61854C21.8937 6.99997 21.798 7.45764 21.6885 7.98866L19.748 17.3959C19.6384 17.9272 19.5446 18.3862 19.4365 18.7553C19.3251 19.1353 19.1751 19.506 18.9102 19.8373C18.5137 20.3326 17.9709 20.6886 17.3613 20.8558C16.9521 20.9681 16.5527 20.9587 16.1592 20.9105C16.064 20.8989 15.9641 20.8838 15.8594 20.8666C15.8148 21.0336 15.7556 21.1992 15.6729 21.3617C15.3847 21.9271 14.9246 22.3852 14.3613 22.6722C13.9832 22.8649 13.5905 22.9368 13.1953 22.9691C12.8118 23.0005 12.3433 22.9994 11.7998 22.9994H6.2002C5.65664 22.9994 5.18845 23.0005 4.80469 22.9691C4.40942 22.9368 4.01603 22.865 3.6377 22.6722C3.07342 22.3846 2.61471 21.926 2.32715 21.3617C2.13451 20.9835 2.06257 20.5899 2.03027 20.1947C1.99897 19.811 2 19.3425 2 18.7992V9.19959C2 8.65626 1.99893 8.18748 2.03027 7.80409C2.06262 7.40908 2.13455 7.01606 2.32715 6.63807C2.61419 6.07493 3.07237 5.61466 3.6377 5.32655C4.01575 5.13396 4.40895 5.06195 4.80371 5.02967C5.18699 4.99835 5.65482 4.9994 6.19727 4.9994H8.01074C8.10641 4.53628 8.19115 4.13298 8.28809 3.80213C8.39958 3.42173 8.54941 3.05144 8.81445 2.7201C9.20937 2.22644 9.75131 1.86855 10.3633 1.70057ZM6.19727 6.9994C5.62184 6.9994 5.24973 6.99972 4.9668 7.02284C4.69628 7.04494 4.59527 7.08272 4.5459 7.1078C4.35894 7.20306 4.20581 7.3572 4.10937 7.54627C4.08415 7.59581 4.04562 7.69653 4.02344 7.96717C4.0003 8.25021 4 8.62329 4 9.19959V18.7992C4 19.3754 4.0013 19.7482 4.02441 20.0316C4.04656 20.3027 4.08422 20.404 4.10937 20.4535C4.20521 20.6415 4.35788 20.7942 4.5459 20.89C4.59528 20.9152 4.696 20.9528 4.96777 20.975C5.25123 20.9981 5.62365 20.9994 6.2002 20.9994H11.7998C12.3764 20.9994 12.7491 20.9991 13.0322 20.976C13.3032 20.9538 13.4036 20.9153 13.4531 20.89C13.6423 20.7936 13.7963 20.6405 13.8916 20.4535C13.9167 20.4041 13.9545 20.3028 13.9766 20.0326C13.9836 19.9463 13.9878 19.8516 13.9912 19.7455C13.9871 19.6835 13.9882 19.6205 13.9961 19.557C13.9995 19.3505 14 19.1041 14 18.8021V9.19666C14 8.6218 13.9996 8.24974 13.9766 7.96717C13.9545 7.69725 13.9167 7.59571 13.8916 7.54627C13.7957 7.35828 13.6412 7.20361 13.4531 7.1078C13.4036 7.08266 13.3021 7.04491 13.0322 7.02284C12.7496 6.99976 12.3778 6.9994 11.8027 6.9994H6.19727ZM11.3223 3.63026C11.0526 3.59722 10.9459 3.61468 10.8926 3.62928C10.6903 3.68488 10.5095 3.80439 10.377 3.9701C10.3422 4.01352 10.2844 4.10401 10.208 4.36463C10.1588 4.53262 10.1112 4.73611 10.0547 4.9994H11.8027C12.345 4.9994 12.8123 4.99838 13.1953 5.02967C13.5901 5.06195 13.9833 5.13398 14.3613 5.32655C14.9256 5.61409 15.3852 6.07384 15.6729 6.63807C15.8654 7.016 15.9374 7.40938 15.9697 7.80409C16.001 8.18702 16 8.6546 16 9.19666V18.8021C16 18.8213 15.999 18.8408 15.999 18.8597C16.1535 18.8882 16.285 18.9108 16.4023 18.9252C16.6722 18.9582 16.7784 18.9409 16.832 18.9262C17.0367 18.8699 17.2185 18.7512 17.3496 18.5873C17.3841 18.544 17.4414 18.4529 17.5176 18.1928C17.5974 17.9204 17.6728 17.5559 17.7891 16.9926L19.7295 7.58436C19.8456 7.02158 19.9206 6.65771 19.9551 6.37635C19.988 6.10732 19.9716 6.00021 19.957 5.94666C19.9012 5.74301 19.7811 5.56002 19.6162 5.42811C19.5727 5.39348 19.4811 5.33617 19.2217 5.26014C18.9495 5.18043 18.5848 5.10486 18.0215 4.98866L12.5322 3.85584C11.9686 3.73958 11.604 3.66478 11.3223 3.63026Z',
        },
      ],
    ],
    f: 1,
  },
  heart: {
    e: [
      [
        'path',
        {
          d: 'M2 9.5a5.5 5.5 0 0 1 9.591-3.676.56.56 0 0 0 .818 0A5.49 5.49 0 0 1 22 9.5c0 2.29-1.5 4-3 5.5l-5.492 5.313a2 2 0 0 1-3 .019L5 15c-1.5-1.5-3-3.2-3-5.5',
        },
      ],
    ],
  },
  house: {
    e: [
      ['path', { d: 'M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8' }],
      [
        'path',
        {
          d: 'M3 10a2 2 0 0 1 .709-1.528l7-6a2 2 0 0 1 2.582 0l7 6A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
        },
      ],
    ],
  },
  image: {
    e: [
      ['rect', { width: '18', height: '18', x: '3', y: '3', rx: '2', ry: '2' }],
      ['circle', { cx: '9', cy: '9', r: '2' }],
      ['path', { d: 'm21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21' }],
    ],
  },
  'image-ads': {
    e: [
      [
        'path',
        {
          d: 'M17.7998 2C18.3433 2 18.8117 1.99892 19.1953 2.03027C19.5906 2.06259 19.984 2.13441 20.3623 2.32715C20.926 2.61445 21.385 3.07289 21.6729 3.6377C21.8655 4.01581 21.9374 4.40888 21.9697 4.80371C22.001 5.187 22 5.6548 22 6.19727V13.0195C22 13.5718 21.5523 14.0195 21 14.0195C20.4477 14.0195 20 13.5718 20 13.0195V6.19727C20 5.62182 19.9997 5.24974 19.9766 4.9668C19.9544 4.69595 19.9167 4.5952 19.8916 4.5459C19.7961 4.35841 19.6427 4.20555 19.4541 4.10938C19.4046 4.08416 19.3037 4.0466 19.0322 4.02441C18.749 4.00127 18.3764 4 17.7998 4H6.2002C5.62365 4 5.25123 4.00126 4.96777 4.02441C4.69597 4.04662 4.59528 4.08421 4.5459 4.10938C4.35779 4.20524 4.20524 4.35779 4.10937 4.5459C4.08421 4.59528 4.04662 4.69597 4.02441 4.96777C4.00126 5.25123 4 5.62365 4 6.2002V15.2969L7.00879 11.7871L7.00977 11.7852C7.20962 11.552 7.4008 11.3273 7.57617 11.1553C7.75814 10.9768 7.99743 10.7789 8.32129 10.6641C8.70792 10.527 9.12533 10.5126 9.51758 10.6201L9.68359 10.6738L9.80078 10.7217C10.0642 10.8426 10.2646 11.0172 10.4219 11.1758C10.593 11.3484 10.7791 11.5722 10.9736 11.8057L12.998 14.1846C13.356 14.6051 13.3061 15.2367 12.8857 15.5947C12.4652 15.9527 11.8336 15.9019 11.4756 15.4814L9.4502 13.1025L9.44336 13.0947C9.22362 12.831 9.09973 12.6826 9.00195 12.584C8.99768 12.5797 8.99311 12.5761 8.98926 12.5723C8.98547 12.5759 8.98076 12.5799 8.97656 12.584C8.87758 12.6811 8.75103 12.8271 8.52832 13.0869L8.52734 13.0879L4.00293 18.3652C4.00548 18.6719 4.01102 18.8971 4.02832 19.0791C4.05075 19.3151 4.08584 19.4069 4.10937 19.4531C4.2058 19.6424 4.35882 19.7963 4.5459 19.8916C4.5952 19.9167 4.69595 19.9544 4.9668 19.9766C5.24974 19.9997 5.62182 20 6.19727 20H14C14.5523 20 15 20.4477 15 21C15 21.5523 14.5523 22 14 22H6.19727C5.6548 22 5.187 22.001 4.80371 21.9697C4.40888 21.9374 4.01581 21.8655 3.6377 21.6729C3.07225 21.3847 2.61418 20.9246 2.32715 20.3613C2.14609 20.006 2.07229 19.6386 2.03711 19.2686C2.00304 18.9101 2.00038 18.4849 2 18.001V6.2002C2 5.65664 1.99893 5.18845 2.03027 4.80469C2.06258 4.40941 2.13437 4.01605 2.32715 3.6377C2.61475 3.07333 3.07333 2.61475 3.6377 2.32715C4.01605 2.13437 4.40941 2.06258 4.80469 2.03027C5.18845 1.99893 5.65664 2 6.2002 2H17.7998ZM15 6C16.1046 6 17 6.89543 17 8C17 9.10457 16.1046 10 15 10C13.8954 10 13 9.10457 13 8C13 6.89543 13.8954 6 15 6Z',
        },
      ],
      [
        'path',
        {
          d: 'M17.3373 13.5454C17.397 13.3598 17.6596 13.3599 17.7193 13.5454L18.1732 14.9585C18.5202 16.0392 19.3676 16.8859 20.4483 17.2329L21.8607 17.6868C22.0464 17.7464 22.0464 18.0092 21.8607 18.0688L20.4483 18.5226C19.3676 18.8697 18.5202 19.717 18.1732 20.7977L17.7193 22.2102C17.6597 22.3959 17.3969 22.3959 17.3373 22.2102L16.8835 20.7977C16.5364 19.717 15.6897 18.8697 14.609 18.5226L13.1959 18.0688C13.0104 18.009 13.0104 17.7465 13.1959 17.6868L14.609 17.2329C15.6896 16.8859 16.5364 16.0391 16.8835 14.9585L17.3373 13.5454Z',
        },
      ],
    ],
    f: 1,
  },
  'image-plus': {
    e: [
      ['path', { d: 'M16 5h6' }],
      ['path', { d: 'M19 2v6' }],
      ['path', { d: 'M21 11.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7.5' }],
      ['path', { d: 'm21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21' }],
      ['circle', { cx: '9', cy: '9', r: '2' }],
    ],
  },
  images: {
    e: [
      ['path', { d: 'm22 11-1.296-1.296a2.4 2.4 0 0 0-3.408 0L11 16' }],
      ['path', { d: 'M4 8a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2' }],
      ['circle', { cx: '13', cy: '7', r: '1', fill: 'currentColor' }],
      ['rect', { x: '8', y: '2', width: '14', height: '14', rx: '2' }],
    ],
  },
  info: {
    e: [
      ['circle', { cx: '12', cy: '12', r: '10' }],
      ['path', { d: 'M12 16v-4' }],
      ['path', { d: 'M12 8h.01' }],
    ],
  },
  layers: {
    e: [
      [
        'path',
        {
          d: 'M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z',
        },
      ],
      ['path', { d: 'M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12' }],
      ['path', { d: 'M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17' }],
    ],
  },
  lightning: {
    e: [
      [
        'path',
        {
          d: 'M13.4818 1.84988C13.9797 1.53962 14.5541 1.65225 14.9184 1.95926C15.3021 2.28291 15.5074 2.86227 15.2553 3.43191L12.9613 8.60867H16.2621C17.5125 8.60926 17.9078 10.1785 17.0033 10.8626L7.45059 18.0813C6.9316 18.4735 6.30399 18.3678 5.91543 18.0403C5.5316 17.7166 5.32607 17.1365 5.57852 16.5667L7.87344 11.3909H4.57266C3.32201 11.3907 2.92595 9.82123 3.83047 9.13699L13.3832 1.91824L13.4818 1.84988Z',
        },
      ],
    ],
    f: 1,
    b: 20,
  },
  'log-out': {
    e: [
      ['path', { d: 'm16 17 5-5-5-5' }],
      ['path', { d: 'M21 12H9' }],
      ['path', { d: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4' }],
    ],
  },
  'message-circle': {
    e: [
      [
        'path',
        {
          d: 'M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719',
        },
      ],
    ],
  },
  'panel-left': {
    e: [
      ['rect', { width: '18', height: '18', x: '3', y: '3', rx: '2' }],
      ['path', { d: 'M9 3v18' }],
    ],
  },
  pause: {
    e: [
      ['rect', { x: '14', y: '3', width: '5', height: '18', rx: '1' }],
      ['rect', { x: '5', y: '3', width: '5', height: '18', rx: '1' }],
    ],
  },
  pencil: {
    e: [
      [
        'path',
        {
          d: 'M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z',
        },
      ],
      ['path', { d: 'm15 5 4 4' }],
    ],
  },
  play: {
    e: [
      [
        'path',
        { d: 'M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z' },
      ],
    ],
  },
  plus: {
    e: [
      ['path', { d: 'M5 12h14' }],
      ['path', { d: 'M12 5v14' }],
    ],
  },
  'redo-2': {
    e: [
      ['path', { d: 'm15 14 5-5-5-5' }],
      ['path', { d: 'M20 9H9.5A5.5 5.5 0 0 0 4 14.5A5.5 5.5 0 0 0 9.5 20H13' }],
    ],
  },
  search: {
    e: [
      ['path', { d: 'm21 21-4.34-4.34' }],
      ['circle', { cx: '11', cy: '11', r: '8' }],
    ],
  },
  'shield-check': {
    e: [
      [
        'path',
        {
          d: 'M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z',
        },
      ],
      ['path', { d: 'm9 12 2 2 4-4' }],
    ],
  },
  sparkles: {
    e: [
      [
        'path',
        {
          d: 'M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z',
        },
      ],
      ['path', { d: 'M20 2v4' }],
      ['path', { d: 'M22 4h-4' }],
      ['circle', { cx: '4', cy: '20', r: '2' }],
    ],
  },
  store: {
    e: [
      ['path', { d: 'M15 21v-5a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v5' }],
      [
        'path',
        {
          d: 'M17.774 10.31a1.12 1.12 0 0 0-1.549 0 2.5 2.5 0 0 1-3.451 0 1.12 1.12 0 0 0-1.548 0 2.5 2.5 0 0 1-3.452 0 1.12 1.12 0 0 0-1.549 0 2.5 2.5 0 0 1-3.77-3.248l2.889-4.184A2 2 0 0 1 7 2h10a2 2 0 0 1 1.653.873l2.895 4.192a2.5 2.5 0 0 1-3.774 3.244',
        },
      ],
      ['path', { d: 'M4 10.95V19a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8.05' }],
    ],
  },
  'trash-2': {
    e: [
      ['path', { d: 'M10 11v6' }],
      ['path', { d: 'M14 11v6' }],
      ['path', { d: 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6' }],
      ['path', { d: 'M3 6h18' }],
      ['path', { d: 'M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2' }],
    ],
  },
  type: {
    e: [
      ['path', { d: 'M12 4v16' }],
      ['path', { d: 'M4 7V5a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v2' }],
      ['path', { d: 'M9 20h6' }],
    ],
  },
  'undo-2': {
    e: [
      ['path', { d: 'M9 14 4 9l5-5' }],
      ['path', { d: 'M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5a5.5 5.5 0 0 1-5.5 5.5H11' }],
    ],
  },
  'video-ads': {
    e: [
      [
        'path',
        {
          d: 'M16.7998 2C17.3433 2 17.8117 1.99892 18.1953 2.03027C18.5906 2.06258 18.984 2.13441 19.3623 2.32714C19.9259 2.61445 20.385 3.07289 20.6728 3.63769C20.8655 4.0158 20.9374 4.40888 20.9697 4.80371C21.001 5.18699 21 5.6548 21 6.19726V13.0195C21 13.5718 20.5523 14.0195 20 14.0195C19.4477 14.0195 19 13.5718 19 13.0195V6.19726C19 5.62182 18.9997 5.24973 18.9766 4.96679C18.9544 4.69594 18.9167 4.5952 18.8916 4.54589C18.7961 4.35841 18.6427 4.20554 18.4541 4.10937C18.4046 4.08416 18.3037 4.0466 18.0322 4.02441C17.749 4.00126 17.3764 4 16.7998 4H7.20019C6.62365 4 6.25123 4.00126 5.96777 4.02441C5.69597 4.04661 5.59527 4.08421 5.54589 4.10937C5.35779 4.20523 5.20523 4.35779 5.10937 4.54589C5.08421 4.59527 5.04661 4.69597 5.02441 4.96777C5.00126 5.25123 5 5.62365 5 6.20019V17.999C5.00039 18.5034 5.00477 18.8314 5.02832 19.0791C5.05075 19.3151 5.08584 19.4069 5.10937 19.4531C5.20579 19.6424 5.35882 19.7963 5.54589 19.8916C5.5952 19.9167 5.69594 19.9544 5.96679 19.9766C6.24973 19.9997 6.62182 20 7.19726 20H13C13.5523 20 14 20.4477 14 21C14 21.5523 13.5523 22 13 22H7.19726C6.6548 22 6.18699 22.001 5.80371 21.9697C5.40888 21.9374 5.0158 21.8655 4.63769 21.6728C4.07225 21.3847 3.61417 20.9246 3.32714 20.3613C3.14609 20.006 3.07229 19.6386 3.0371 19.2686C3.00303 18.9101 3.00037 18.4849 3 18.001V6.20019C3 5.65663 2.99892 5.18844 3.03027 4.80468C3.06257 4.40941 3.13437 4.01604 3.32714 3.63769C3.61474 3.07333 4.07333 2.61474 4.63769 2.32714C5.01604 2.13437 5.40941 2.06257 5.80468 2.03027C6.18844 1.99892 6.65663 2 7.20019 2H16.7998Z',
        },
      ],
      [
        'path',
        {
          d: 'M16.8679 13.5454C16.9276 13.3598 17.1902 13.3599 17.2499 13.5454L17.7038 14.9585C18.0509 16.0392 18.8982 16.8859 19.9789 17.2329L21.3914 17.6868C21.5771 17.7464 21.5771 18.0092 21.3914 18.0688L19.9789 18.5226C18.8982 18.8697 18.0509 19.717 17.7038 20.7977L17.2499 22.2102C17.1903 22.3959 16.9276 22.3959 16.8679 22.2102L16.4141 20.7977C16.067 19.717 15.2203 18.8697 14.1396 18.5226L12.7265 18.0688C12.5411 18.009 12.541 17.7465 12.7265 17.6868L14.1396 17.2329C15.2203 16.8859 16.067 16.0391 16.4141 14.9585L16.8679 13.5454Z',
        },
      ],
      ['path', { d: 'M10.6636 8.94253V13.5597L14.5112 11.2511L10.6636 8.94253Z' }],
    ],
    f: 1,
  },
  x: {
    e: [
      ['path', { d: 'M18 6 6 18' }],
      ['path', { d: 'm6 6 12 12' }],
    ],
  },
} satisfies Record<string, Glyph>;

export type IconName = keyof typeof GLYPHS;

/** Every name, sorted — for a catalogue, or to assert against the product. */
export const ICON_NAMES = Object.keys(GLYPHS).sort() as IconName[];

export interface IconProps extends Omit<ComponentProps<'svg'>, 'name'> {
  name: IconName;
  /**
   * Edge length in px, 24 by default. A `size-*` class overrides it, because a
   * class sets CSS and this sets the attribute — which is what lets a wrapper
   * size its icon without every caller passing a number.
   */
  size?: number;
}

export function Icon({ name, size = 24, className, ...props }: IconProps) {
  const glyph: Glyph = GLYPHS[name];
  const box = glyph.b ?? 24;
  const filled = glyph.f === 1;

  return (
    <svg
      viewBox={`0 0 ${box} ${box}`}
      width={size}
      height={size}
      fill={filled ? 'currentColor' : 'none'}
      stroke={filled ? 'none' : 'currentColor'}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={cn('flex-none', className)}
      {...props}
    >
      {glyph.e.map(([tag, attrs], index) => {
        const key = `${tag}-${index}`;
        if (tag === 'circle') return <circle key={key} {...attrs} />;
        if (tag === 'rect') return <rect key={key} {...attrs} />;
        if (tag === 'line') return <line key={key} {...attrs} />;
        return <path key={key} {...attrs} />;
      })}
    </svg>
  );
}
